/**
 * EventClient - Main browser client for event tracking
 * Lightweight client that captures identity and sends events to server
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

import {
  getOrCreateAnonymousId,
  readIdentityCookies,
  getCookie,
  setCookie,
  deleteCookie,
  COOKIE_NAMES,
  type CookieOptions,
} from './cookies';
import { captureClickIds, type ClickIds, readClickIds } from './identity';
import { collectPageContext, getUserAgent, type PageContext } from './context';
import { sendEvent, type ClientEvent, type SendResult, type EcommerceItem } from './send';

export interface EventClientConfig {
  /** Server endpoint URL for receiving events */
  endpoint: string;
  /** Cookie options for identity persistence */
  cookieOptions?: CookieOptions;
  /** Request timeout in milliseconds (default: 5000) */
  timeout?: number;
  /** Custom headers to include with requests */
  headers?: Record<string, string>;
  /** Whether to auto-capture click IDs on init (default: true) */
  autoCapture?: boolean;
  /** Mirror click IDs to localStorage as a cookie-survivability fallback (default: false) */
  persistToLocalStorage?: boolean;
  /** Enable debug logging (default: false) */
  debug?: boolean;
  /** This storefront's own GA4 measurement ID (e.g. "G-Q370D478VG").
   *  Binds ga_session_id capture to OUR stream's `_ga_<MID>` cookie. Without
   *  it the SDK scans for the first `_ga_*` cookie, which captures the WRONG
   *  property's session when any second Google tag sets its own cookie on the
   *  domain (blog streams, popup/marketing tools) — purchases then land in
   *  GA4 as Unassigned. Always set this in production. */
  ga4MeasurementId?: string;
}

export interface TrackOptions {
  /** Override the auto-generated event ID with a deterministic one.
   *  Use deterministicEventId() to generate IDs that match server-side events
   *  for deduplication across client/server/pixel bridge. */
  event_id?: string;
  /** Custom event properties */
  properties?: Record<string, unknown>;
  /** Ecommerce data */
  ecommerce?: ClientEvent['ecommerce'];
  /** Override page context */
  context?: Partial<PageContext>;
}

export interface UserData {
  /** User's email address (will be passed to pixels for matching) */
  email?: string;
  /** User's phone number (E.164 format preferred) */
  phone?: string;
  /** External/customer ID from your system */
  external_id?: string;
  /** User's first name */
  first_name?: string;
  /** User's last name */
  last_name?: string;
  /** User's city */
  city?: string;
  /** User's state/region */
  state?: string;
  /** User's postal/zip code */
  zip?: string;
  /** User's country (ISO 3166-1 alpha-2 preferred) */
  country?: string;
}

/**
 * Browser event tracking client
 *
 * @example
 * ```typescript
 * const tracker = new EventClient({
 *   endpoint: '/api/events',
 * });
 *
 * // Track page view
 * tracker.page();
 *
 * // Track custom event
 * tracker.track('add_to_cart', {
 *   ecommerce: {
 *     currency: 'USD',
 *     value: 49.99,
 *     items: [{ product_id: 'PROD-001', quantity: 1 }],
 *   },
 * });
 *
 * // Track purchase
 * tracker.track('purchase', {
 *   ecommerce: {
 *     currency: 'USD',
 *     value: 299.99,
 *     order_id: 'ORD-123',
 *     items: [...],
 *   },
 * });
 * ```
 */
export class EventClient {
  private config: EventClientConfig;
  private anonymousId: string | undefined;
  private initialized = false;
  private userData: UserData = {};
  private hashedExternalId: string | undefined;

  constructor(config: EventClientConfig) {
    this.config = {
      autoCapture: true,
      debug: false,
      persistToLocalStorage: false,
      timeout: 5000,
      ...config,
    };

    if (typeof window !== 'undefined') {
      this.init();
    }
  }

  /**
   * Initialize the tracker
   * Called automatically in browser environment
   */
  private init(): void {
    if (this.initialized) return;

    // Capture click IDs from URL and persist to cookies
    if (this.config.autoCapture) {
      this.captureClickIdsFromUrl();
    }

    // Get or create anonymous ID
    this.anonymousId = getOrCreateAnonymousId(this.config.cookieOptions);

    // Restore user data from cookie (set via setUser in previous page loads)
    this.restoreUserData();

    this.initialized = true;

    if (this.config.debug) {
      console.log('[EventClient] Initialized', {
        anonymousId: this.anonymousId,
        identity: this.getIdentity(),
      });
    }
  }

  /**
   * Capture click IDs from current URL and persist to cookies
   * (and localStorage when opted in).
   */
  captureClickIdsFromUrl(): ClickIds {
    return captureClickIds(this.config.cookieOptions, this.config.persistToLocalStorage);
  }

  /**
   * Get current identity data
   */
  getIdentity(): ClientEvent['identity'] {
    const cookies = readIdentityCookies(this.config.ga4MeasurementId);
    const clickIds = readClickIds(this.config.persistToLocalStorage);

    return {
      anonymous_id: this.anonymousId,
      fbp: cookies.fbp,
      fbc: clickIds.fbc,
      ttclid: clickIds.ttclid,
      ttp: clickIds.ttp,
      gclid: clickIds.gclid,
      ga_client_id: cookies.ga_client_id,
      ga_session_id: cookies.ga_session_id,
      epik: clickIds.epik,
      msclkid: clickIds.msclkid,
      criteo_id: cookies.criteo_id,
    };
  }

  /**
   * Set user data for enhanced matching
   * Call this when user logs in or user data becomes available
   *
   * @example
   * ```typescript
   * tracker.setUser({
   *   email: 'user@example.com',
   *   external_id: 'customer_123',
   *   first_name: 'John',
   *   last_name: 'Doe',
   * });
   * ```
   */
  setUser(userData: UserData): void {
    this.userData = { ...this.userData, ...userData };
    this.persistUserData();

    // Pre-compute SHA256 of external_id for platforms that require it (e.g., TikTok)
    if (userData.external_id) {
      this.computeExternalIdHash(userData.external_id);
    }

    if (this.config.debug) {
      console.log('[EventClient] User data set:', {
        hasEmail: !!userData.email,
        hasPhone: !!userData.phone,
        hasExternalId: !!userData.external_id,
        hasFirstName: !!userData.first_name,
        hasLastName: !!userData.last_name,
        hasAddress: !!(userData.city || userData.state || userData.zip || userData.country),
      });
    }
  }

  /**
   * Compute SHA256 hash of external_id using Web Crypto API.
   * Fire-and-forget — the hash will be ready by the time the next event fires.
   */
  private computeExternalIdHash(externalId: string): void {
    if (typeof crypto === 'undefined' || !crypto.subtle) {
      this.hashedExternalId = undefined;
      return;
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(externalId);
    crypto.subtle.digest('SHA-256', data).then((hashBuffer) => {
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      this.hashedExternalId = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    }).catch(() => {
      this.hashedExternalId = undefined;
    });
  }

  /**
   * Clear user data (call on logout)
   */
  clearUser(): void {
    this.userData = {};
    this.hashedExternalId = undefined;
    deleteCookie(COOKIE_NAMES.USER_DATA, this.config.cookieOptions);

    if (this.config.debug) {
      console.log('[EventClient] User data cleared');
    }
  }

  /**
   * Persist user data to a first-party cookie so it survives page reloads.
   */
  private persistUserData(): void {
    const data: Record<string, string> = {};
    if (this.userData.email) data.e = this.userData.email;
    if (this.userData.phone) data.p = this.userData.phone;
    if (this.userData.first_name) data.fn = this.userData.first_name;
    if (this.userData.last_name) data.ln = this.userData.last_name;
    if (this.userData.external_id) data.id = this.userData.external_id;
    if (this.userData.city) data.ct = this.userData.city;
    if (this.userData.state) data.st = this.userData.state;
    if (this.userData.zip) data.z = this.userData.zip;
    if (this.userData.country) data.co = this.userData.country;

    if (Object.keys(data).length > 0) {
      setCookie(COOKIE_NAMES.USER_DATA, JSON.stringify(data), {
        ...this.config.cookieOptions,
        maxAgeDays: 30,
      });
    }
  }

  /**
   * Restore user data from cookie on initialization.
   */
  private restoreUserData(): void {
    const raw = getCookie(COOKIE_NAMES.USER_DATA);
    if (!raw) return;

    try {
      const data = JSON.parse(raw);
      this.userData = {
        email: data.e,
        phone: data.p,
        first_name: data.fn,
        last_name: data.ln,
        external_id: data.id,
        city: data.ct,
        state: data.st,
        zip: data.z,
        country: data.co,
      };

      // Re-compute SHA256 hash for restored external_id
      if (data.id) {
        this.computeExternalIdHash(data.id);
      }

      if (this.config.debug) {
        console.log('[EventClient] User data restored from cookie:', {
          hasEmail: !!data.e,
          hasPhone: !!data.p,
          hasExternalId: !!data.id,
          hasAddress: !!(data.ct || data.st || data.z || data.co),
        });
      }
    } catch {
      // Invalid cookie data, ignore
    }
  }

  /**
   * Get current user data
   */
  getUser(): UserData {
    return { ...this.userData };
  }

  /**
   * Generate a unique event ID
   */
  private generateEventId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Build client event payload
   */
  private buildEvent(eventName: string, options: TrackOptions = {}): ClientEvent {
    const pageContext = collectPageContext();

    // Only include user_data if we have any values
    const hasUserData = Object.values(this.userData).some(v => v !== undefined);

    return {
      event_name: eventName,
      event_time: Math.floor(Date.now() / 1000),
      event_id: options.event_id ?? this.generateEventId(),
      context: {
        page_url: options.context?.page_url ?? pageContext.page_url,
        page_title: options.context?.page_title ?? pageContext.page_title,
        referrer: options.context?.referrer ?? pageContext.referrer,
        site_type: options.context?.site_type ?? pageContext.site_type,
        locale: pageContext.locale,
        timezone: pageContext.timezone,
        viewport_width: pageContext.viewport_width,
        viewport_height: pageContext.viewport_height,
      },
      identity: this.getIdentity(),
      user_data: hasUserData ? { ...this.userData } : undefined,
      user_agent: getUserAgent(),
      properties: options.properties,
      ecommerce: options.ecommerce,
    };
  }

  /**
   * Push event to window.dataLayer for GTM
   * Enables client-side pixels to fire with matching event_id for deduplication
   * Includes identity data (cookies/click IDs) and user data for enhanced matching
   */
  private pushToDataLayer(event: ClientEvent): void {
    if (typeof window === 'undefined') return;

    const identity = this.getIdentity();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: `ues_${event.event_name}`,
      event_id: event.event_id,
      ecommerce: event.ecommerce,
      properties: event.properties,
      page_url: event.context.page_url,
      page_title: event.context.page_title,
      // Identity data (cookies and click IDs)
      user_data: {
        // User PII for enhanced matching (pixels will hash as needed)
        email: this.userData.email,
        phone: this.userData.phone,
        first_name: this.userData.first_name,
        last_name: this.userData.last_name,
        external_id: this.userData.external_id,
        // Address fields for enhanced matching
        city: this.userData.city,
        state: this.userData.state,
        zip: this.userData.zip,
        country: this.userData.country,
        // SHA256-hashed external_id for platforms that require pre-hashing (TikTok)
        external_id_sha256: this.hashedExternalId,
        // Platform-specific identifiers
        fbp: identity.fbp,
        fbc: identity.fbc,
        ttp: identity.ttp,
        ttclid: identity.ttclid,
        ga_client_id: identity.ga_client_id,
        ga_session_id: identity.ga_session_id,
        gclid: identity.gclid,
        criteo_id: identity.criteo_id,
        epik: identity.epik,
        // Anonymous ID
        anonymous_id: identity.anonymous_id,
      },
    });

    if (this.config.debug) {
      console.log(`[EventClient] dataLayer push: ues_${event.event_name}`, event.event_id);
    }
  }

  /**
   * Track a custom event
   */
  async track(eventName: string, options: TrackOptions = {}): Promise<SendResult> {
    const event = this.buildEvent(eventName, options);

    // Push to dataLayer first (non-blocking, for GTM/client-side pixels)
    this.pushToDataLayer(event);

    if (this.config.debug) {
      console.log(`[EventClient] Tracking: ${eventName}`, event);
    }

    const result = await sendEvent(event, {
      endpoint: this.config.endpoint,
      timeout: this.config.timeout,
      headers: this.config.headers,
    });

    if (this.config.debug) {
      const status = result.success ? '✓' : '✗';
      console.log(`[EventClient] ${status} ${eventName}`, result);
    }

    return result;
  }

  /**
   * Track a page view
   */
  async page(options: TrackOptions = {}): Promise<SendResult> {
    return this.track('page_view', options);
  }

  /**
   * Track a product view
   */
  async viewProduct(
    product: {
      product_id: string;
      product_name?: string;
      variant_id?: string;
      sku?: string;
      price?: number;
      category?: string;
    },
    options: TrackOptions = {}
  ): Promise<SendResult> {
    return this.track('product_viewed', {
      ...options,
      ecommerce: {
        ...options.ecommerce,
        items: [{ ...product, quantity: 1 }],
      },
    });
  }

  /**
   * Track add to cart
   */
  async addToCart(
    item: {
      product_id: string;
      product_name?: string;
      variant_id?: string;
      sku?: string;
      price?: number;
      quantity?: number;
      category?: string;
      is_swatch?: boolean;
    },
    options: TrackOptions = {}
  ): Promise<SendResult> {
    return this.track('add_to_cart', {
      ...options,
      ecommerce: {
        ...options.ecommerce,
        value: item.price ? item.price * (item.quantity ?? 1) : undefined,
        items: [item],
      },
    });
  }

  /**
   * Track remove from cart
   */
  async removeFromCart(
    item: {
      product_id: string;
      product_name?: string;
      sku?: string;
      price?: number;
      quantity?: number;
    },
    options: TrackOptions = {}
  ): Promise<SendResult> {
    return this.track('remove_from_cart', {
      ...options,
      ecommerce: {
        ...options.ecommerce,
        items: [item],
      },
    });
  }

  /**
   * Track begin checkout
   */
  async beginCheckout(
    cart: {
      currency?: string;
      value?: number;
      items?: EcommerceItem[];
    },
    options: TrackOptions = {}
  ): Promise<SendResult> {
    return this.track('checkout_started', {
      ...options,
      ecommerce: {
        currency: cart.currency,
        value: cart.value,
        items: cart.items,
        ...options.ecommerce,
      },
    });
  }

  /**
   * Track a search
   */
  async search(query: string, options: TrackOptions = {}): Promise<SendResult> {
    return this.track('search', {
      ...options,
      properties: {
        search_query: query,
        ...options.properties,
      },
    });
  }

  /**
   * Track a lead/form submission
   */
  async lead(options: TrackOptions = {}): Promise<SendResult> {
    return this.track('lead', options);
  }

  /**
   * Get the anonymous ID (useful for passing to checkout)
   */
  getAnonymousId(): string | undefined {
    return this.anonymousId;
  }
}

/**
 * Create an EventClient instance
 */
export function createEventClient(config: EventClientConfig): EventClient {
  return new EventClient(config);
}
